/**
 * Сервис для работы с пользователями
 */

import axios, { AxiosRequestConfig } from 'axios';

import { IId, ITrackInfo } from '../models/types';
import { pageSize } from '../constants/pagination';
import webSsoConstants from '../constants/webSsoConstants';

export interface IUpdateOrCreateBody {
  id: IId | null;
  tenantId?: IId;
  name: string | null;
  lastName: string;
  secondName: string | null;
  phone: string | null;
  email: string | null;
  birthday: string | null;
  authorizationData: {
    login: string | null;
    password: string | null;
    hasSsoAuth: boolean;
    changePass: boolean;
  };
  workInfo: {
    departmentId: IId | undefined;
    postId: IId | null;
    tabularNumber: string | null;
    protections: IId[];
  };
  settings: {
    role: string;
    planColor: string;
    immobilizedTimeMinutes: number;
    workshiftDurationHours: number;
    workshiftBreakMinutes: number;
    managedDepartments: any;
    dataAccesses: any;
  };
  trackerInfo?: {
    deviceId: number;
    deviceCharge: string;
  };
  photoContent?: string;
  photoContentType?: string;
  trackInfo?: ITrackInfo;
  charge?: {
    percent: number;
    isCharging: boolean;
    time: string;
    volt: any;
  };
}

export interface IGetAllParams {
  fullTextSearch?: string;
  departmentId?: string;
  device?: string;
  roles?: string;
  lastPosition?: string;
  posts?: string;
  includePreview?: boolean;
  pageIndex?: number;
  pageSize?: number;
}

export interface IGetBrigadeAllParams {
  fullTextSearch?: string;
  includeOther?: boolean;
  pageIndex?: number;
  pageSize?: number;
}

export interface IGetDepartmentsAllParams {
  fullTextSearch?: string;
  device?: string;
  lastPosition?: string;
  departmentId?: IId;
  includeEmptyDepartment?: boolean;
  includeEmployees?: boolean;
  pageIndex?: number;
  pageSize?: number;
}

export interface IBody {
  username: string;
  password: string;
}

export interface IUserBody {
  id: IId;
  lastname: string;
  name: string;
  secondname: string;
  birthday: string;
  username: string;
  brigadeId: number;
  lead?: string;
  title: string;
  tabularNumber: string;
  mapColor: string;
  protectionsList?: IId[];
  descr: string;
  avatar?: File;
}

export interface IBrigadeBody {
  id: IId;
  name: string;
}
export interface IDepartmentBody {
  id: IId;
  name: string;
  parentId?: IId;
  employees?: IId[];
}

export interface IRoleBody {
  id: IId;
  name: string;
  friendlyName: string;
}

export interface ISectreeACL {
  uid: IId;
  roleId: IId;
  action: string;
  result: string;
}

export interface IFilterParams {
  fullTextSearch?: string;
  pageSize?: number;
  includePreview?: boolean;
  includeOther?: boolean;
}

export interface IShortUsersParams {
  fullTextSearch?: string;
}

export interface ILoginParams {
  name?: string;
  lastName?: string;
  secondName?: string;
}

export interface IGetByTokenParams {
  preview?: boolean;
}

export interface ISecurableTree {
  uid?: IId;
  parent: IId;
  type: string;
  systemName: string;
  friendlyName: string;
  hash?: string;
  clientVersion?: string;
}

export interface IUpdateOrCreateBrigadeBody {
  id: IId;
  name: string;
  leadId: IId;
  employees: Array<{
    userId: IId;
    fio: string;
  }>;
}
const createCsvConfig = (filter: object): AxiosRequestConfig => ({
  params: { ...filter },
  responseType: 'blob'
});
const url = '/user';

const UsersService = {
  getToken: (code: string) =>
    axios.get(`${url}/sso_callback`, {
      params: { code, redirect_uri: webSsoConstants.redirectUri }
    }),
  getRefreshedToken: (refreshToken: string | null) =>
    axios.post(`v0/auth/refresh_token?refreshToken=${refreshToken}`),
  get: (userName: string) => axios.get(`${url}/info/${userName}`, { params: { preview: true } }),
  getUserEmployees: (userId: IId) => axios.get(`/v0/user/info/userId=${userId}`),
  getEmployees: (id: IId) => axios.get(`/v0/employees/${id}`),

  getAll: (params: IGetAllParams) =>
    axios.get(`${url}/infoAll`, { params: { pageSize, ...params } }),
  getAllEmployees: (params: IGetAllParams) =>
    axios.get(`/v0/employees`, { params: { pageSize, ...params } }),
  getEmployeesPresence: (params: {
    scope?: string;
    fts?: string;
    users?: IId[];
    placeId?: IId;
    mapId?: IId;
    areaId?: IId;
    ftsDevice?: string;
    serialNumber?: string;
  } = {}) => axios.get(`/v0/employees/presence`, { params }),
  getCsvAllEmployees: (params: IGetAllParams) =>
    axios.get(`v0/employees/export`, createCsvConfig(params)),
  // getAllShort: (params: IShortUsersParams = {}) => axios.get(`${url}/infoAllShort`, { params }),
  getAllEmployeesShort: (params: IShortUsersParams = {}) =>
    axios.get(`/v0/employees/infoAllShort`, { params }),
  getAllEmployeesFull: (params: IShortUsersParams = {}) =>
    axios.get(`/v0/employees/infoAllFull`, { params }),
  getByToken: (params: IGetByTokenParams = {}) => axios.get(`${url}/info`, { params }), // не используется
  getRoles: () => axios.get(`${url}/roles`),
  getPosts: () => axios.get(`/v0/posts`),
  getRole: (id: IId) => axios.get(`/role/${id}`),
  getBrigade: (id: IId) => axios.get(`${url}/brigades/id=${id}`),
  getDepartment: (id: IId) => axios.get(`/v0/departments/${id}`),
  getBrigadeAll: (params: IGetBrigadeAllParams = {}) =>
    axios.get(`${url}/brigades`, { params: { pageSize, ...params } }),
  getDepartmentsAll: (params: IGetDepartmentsAllParams = {}) =>
    axios.get(`/v0/departments`, { params }),
  getLogin: (params: ILoginParams = {}) => axios.get(`${url}/sampleLogin`, { params }),
  updateOrCreate: (body: IUserBody) => axios.post('/v0/user', body),
  updateOrCreateEmployees: (body: IUpdateOrCreateBody) => axios.post('/v0/employees', body),
  updateOrCreateAvatar: (formData: FormData) => axios.post(`${url}/photo`, formData),
  updateOrCreateBrigade: (body: IBrigadeBody) => axios.post(`${url}/brigades`, body),
  updateOrCreateDepartment: (body: IDepartmentBody) => axios.post('/v0/departments', body),
  updateOrCreateRole: (body: IRoleBody) => axios.post(`/role`, body),
  updateOrCreateACL: (body: ISectreeACL[]) => axios.post(`${url}/sectree/acl`, body),
  importFromFileUserList: (body: FormData) =>
    axios.post(`/v0/employees/import`, body, { responseType: 'blob' }),
  delete: (id: number) => axios.delete(`${url}/info/userId=${id}`),
  deleteEmployees: (id: IId) => axios.delete(`/v0/employees/${id}`),
  deleteRole: (id: IId) => axios.delete(`/role`, { params: { id } }),
  deleteBrigade: (id: IId) => axios.delete(`${url}/brigades?brigadeId=${id}`),
  deleteDepartment: (id: IId) => axios.delete('/v0/departments/' + id),
  login: (body: IBody) => axios.post(`v0/auth/login`, body),
  getSecurableTreeVersion: (root: IId) => axios.get(`${url}/sectree/version`, { params: { root } }),
  getSecurables: (root: IId) => axios.get(`${url}/sectree/right?root=${root}`),
  getSecTreeHierarchy: () => axios.get(`${url}/sectree/hierarchy`),
  createSecurableTree: (root: IId, body: ISecurableTree[]) =>
    axios.post(`${url}/sectree?root=${root}`, body),
  getNodeACL: (root: IId, roleId: IId) =>
    axios.get(`${url}/sectree/nodeacl`, { params: { root, roleId } })
};

export default UsersService;
